import './testDetailsEditComponent.scss'
import React, {useCallback, useEffect, useRef, useState} from 'react';
import * as Yup from "yup";
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from "formik";
import CommonService from "../../../../../shared/services/common.service";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import Grid from "@mui/material/Grid";
import FormikInputComponent from "../../../../../shared/components/core/formik-input/FormikInputComponent";
import FormikSelectDropdownComponent
    from "../../../../../shared/components/core/formik-select-dropdown/FormikSelectDropdownComponent";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import {useDispatch, useSelector} from "react-redux";
import commonService from "../../../../../shared/services/common.service";
import {getSampleTypesList} from "../../../../../store/actions/staticData.action";
import {ImageConfig} from "../../../../../constants";

interface TestPanelDetailsEditComponentProps {
    testDetails: any;
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const testPanelDetailsAddFormValidationSchema = Yup.object({
    name: Yup.string().required("Test Name is Required")
        .max(50, "Test Name should be less than 50 characters"),
    code: Yup.string().required("Test Code is Required")
        .min(4, "Test Code should be Minimum 4 characters")
        .max(4, "Test Code should be Maximum 4 characters")
    ,
    sampleType: Yup.mixed().required("Sample Type Required"),
    sampleQuantity: Yup.string().required("Sample Quantity is Required"),
    // description: Yup.string()
    //     .max(200, "Comments should be less than 200 characters"),
    sampleCollectionDeviceName: Yup.mixed().required("Sample Collection Device Name is Required"),
    biomarkerIds: Yup.array().of(Yup.string()).min(1, "At least one Biomarker is required").required("Biomarker is required"),
    resultingMode: Yup.mixed().required("Resulting Mode is required"),
    // panelResultTemplate: Yup.mixed().required('Required'),
    // bulkImportTemplate: Yup.mixed().required('Required')
});

interface testPanelDetailsAddFormSchema {
    name: string;
    code: string;
    sampleType: string;
    sampleCollectionDeviceName: string;
    // description: string;
    biomarkerIds: string[];
    sampleQuantity?: number;
    resultingMode?: string;
    // panelResultTemplate: any;
    // bulkImportTemplate: any;
}

const FormInitialValues: testPanelDetailsAddFormSchema = {
    name: "",
    code: "",
    sampleType: "",
    sampleCollectionDeviceName: "",
    // description: "",
    biomarkerIds: [],
    sampleQuantity: undefined,
    resultingMode: undefined,
    // panelResultTemplate: null,
    // bulkImportTemplate: null,
}


const TestDetailsEditComponent = (props: TestPanelDetailsEditComponentProps) => {
        const {testDetails, onUpdateComplete, onEditCancel} = props;
        const [testPanelDetailsAddFormInitialValues, setTestPanelDetailsAddFormInitialValues] = useState<testPanelDetailsAddFormSchema>(FormInitialValues);
        const formikRef = useRef<FormikProps<FormikValues>>(null);
        const {sampleTypeWithCollectionDevices} = useSelector((state: any) => state.staticData);

        const [biomarkerList, setBiomarkerList] = useState<any[]>([]);
        const [isBiomarkerListLoading, setIsBiomarkerListLoading] = useState<boolean>(false);
        const [isBiomarkerListLoadingError, setIsBiomarkerListLoadingError] = useState<boolean>(false);
        const [isBiomarkerListLoaded, setIsBiomarkerListLoaded] = useState<boolean>(false);
        const dispatch = useDispatch()

        const checkIfBiomarkerAddedOrDeleted = useCallback((oldBiomarkerIds: number[], newBiomarkerIds: number[]) => {
            const addedIds = newBiomarkerIds.filter(id => !oldBiomarkerIds.includes(id));
            const deletedIds = oldBiomarkerIds.filter(id => !newBiomarkerIds.includes(id));

            return {addedIds, deletedIds}
        }, []);

        const updateReportTypeLayout = useCallback((testDetails: any, {addedIds, deletedIds}: {
            [k: string]: number[]
        }, setSubmitting: (isSubmitting: boolean) => void) => {
            const testLayoutDetails = testDetails?.testLayoutDetails[0];
            const reportTypeFormValue: any = {
                layout: testLayoutDetails?.layout || "-",
                disclaimer: testLayoutDetails?.disclaimer || "-",
                tableTitle: testLayoutDetails?.biomarkerObj?.map((table: any, index: number) => {
                    const tableBiomarkerIds = table?.biomarkerIds;
                    if (index === 0 && addedIds.length > 0) {
                        //if new biomarkers add then automatically assign to table 1
                        tableBiomarkerIds.push(...addedIds)
                    }
                    const removedBiomarkersSet = new Set(deletedIds);
                    const remainingBiomarkerIds = tableBiomarkerIds.filter((id: number) => !removedBiomarkersSet.has(id))
                    return {
                        title: table?.title,
                        biomarkerIds: remainingBiomarkerIds,
                    }
                })
            }
            CommonService._test.addReportLayout(testDetails.id, reportTypeFormValue)
                .then((response: any) => {
                    setSubmitting(false);
                    onUpdateComplete(response.data, "report_type");
                }).catch((error: any) => {
                setSubmitting(false);
                CommonService._alert.showToast(error?.response?.data?.message, 'error');
            })
        }, [onUpdateComplete])

        const onTestEdit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
            const payload = {...values}
            delete payload.biomarker;
            const {addedIds, deletedIds} = checkIfBiomarkerAddedOrDeleted(testDetails?.biomarkerIds, values.biomarkerIds)
            let isBiomarkerEdited = addedIds?.length > 0 || deletedIds?.length > 0;
            if (isBiomarkerEdited) {
                CommonService.openConfirmationDialog(
                    {
                        confirmationTitle: 'Update Test Report',
                        confirmationSubTitle: 'The biomarkers have been altered. To reflect these changes in the report layout, please proceed to update report in section.',
                        disableEscKeyClose: false,
                        disableBackdropClose: true,
                        imagePath: `${ImageConfig.ConfirmationImage}`,
                        direction: "up",
                        yes: {
                            text: "Update Report Layout",
                            color: "primary"
                        },
                        no: {
                            text: "Cancel",
                            color: "primary"
                        }
                    }).then(() => {
                    CommonService._test.editTestDetails(testDetails.id, payload)
                        .then((response: IAPIResponseType<any>) => {
                            updateReportTypeLayout(testDetails, {addedIds, deletedIds}, setSubmitting)
                        })
                        .catch((error: any) => {
                            CommonService.handleErrors(setErrors, error);
                            setSubmitting(false);
                        });
                }).catch(() => {
                    setSubmitting(false);
                })
            } else {
                CommonService._test.editTestDetails(testDetails.id, payload)
                    .then((response: IAPIResponseType<any>) => {
                        setSubmitting(false);
                        onUpdateComplete(response.data, "report_type");//after complete redirect to report type tab
                    })
                    .catch((error: any) => {
                        CommonService.handleErrors(setErrors, error);
                        setSubmitting(false);
                    });
            }
        }, [checkIfBiomarkerAddedOrDeleted, onUpdateComplete, testDetails, updateReportTypeLayout]);

        // const handleTemplateAttachmentUpload = useCallback((fieldName: any, event: any) => {
        //     const file = event?.target?.files[0];
        //     const maxAllowedSize = 5 * 1024 * 1024;
        //     if (event?.target?.files[0].size > maxAllowedSize) {
        //         CommonService._alert.showToast('File size should be under 5MB.', "error");
        //         event.target.value = null;
        //         event.target.files = null;
        //         return;
        //     }
        //     event.target.value = null;
        //     event.target.files = null;
        //
        //     if (fieldName === 'panelResultTemplate') {
        //         if (formikRef.current) {
        //             formikRef.current.setFieldValue('panelResultTemplate', file);
        //         }
        //     } else {
        //         if (formikRef.current) {
        //             formikRef.current.setFieldValue('bulkImportTemplate', file);
        //         }
        //     }
        // }, []);

        const getBiomarkerList = useCallback((sampleType: String, sampleCollectionDeviceName: string) => {
            setIsBiomarkerListLoading(true);
            setIsBiomarkerListLoadingError(false);
            CommonService._test.getBiomarkerList(sampleType, sampleCollectionDeviceName)
                .then((response: IAPIResponseType<any>) => {
                    if (response?.data) {
                        setBiomarkerList(response.data);
                    }
                    setIsBiomarkerListLoading(false);
                    setIsBiomarkerListLoaded(true);
                })
                .catch((error: any) => {
                    setIsBiomarkerListLoading(false);
                    setIsBiomarkerListLoadingError(true);
                });
        }, []);

        useEffect(() => {
            if (testDetails) {
                getBiomarkerList(testDetails?.sampleType, testDetails?.sampleCollectionDeviceName);
                setTestPanelDetailsAddFormInitialValues(testDetails)
            }
        }, [testDetails, getBiomarkerList]);


        useEffect(() => {
            dispatch(getSampleTypesList())
        }, [dispatch])

        return (
            <div className="test-panel-details-edit-screen">
                <Formik
                    validationSchema={testPanelDetailsAddFormValidationSchema}
                    initialValues={testPanelDetailsAddFormInitialValues}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}
                    innerRef={formikRef}
                    onSubmit={onTestEdit}
                >
                    {({isSubmitting, values, dirty, isValid, setFieldValue, touched, errors, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <Form className={"test-panel-details-add-holder"}>
                                <div className="t-form-controls-wrapper pdd-top-5">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'name'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Test Name'}
                                                            placeholder={'Enter Test Name'}
                                                            type={"text"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'code'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Test Code'}
                                                            placeholder={'Enter Test Code'}
                                                            maxLength={4}
                                                            type={"text"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'sampleType'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            label={'Sample Type'}
                                                            options={sampleTypeWithCollectionDevices}
                                                            placeholder={'Select Sample Type'}
                                                            valueKey={'sampleType'}
                                                            required={true}
                                                            getOptionLabel={(option) => (commonService.capitalizeFirstLetter(option.sampleType)) || ''}
                                                            onUpdate={(value: any) => {
                                                                setFieldValue('biomarkerIds', []);
                                                                setFieldValue('sampleCollectionDeviceName', '');
                                                            }
                                                            }
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'sampleCollectionDeviceName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            label={'Sample Collection Device Name'}
                                                            placeholder={'Select Sample Collection Device Name'}
                                                            options={commonService.extractCollectionDevicesFromSampleType(values?.sampleType, sampleTypeWithCollectionDevices)}
                                                            valueKey={'code'}
                                                            required={true}
                                                            getOptionLabel={(option) => (commonService.capitalizeFirstLetterOfEachWord(option.title)) || ''}
                                                            onUpdate={(value: any) => {
                                                                setFieldValue('biomarkerIds', []);
                                                                if (value && values?.sampleType) {
                                                                    getBiomarkerList(values?.sampleType, value);
                                                                }
                                                            }
                                                            }
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'sampleQuantity'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Sample Quantity'}
                                                            placeholder={'Enter Sample Quantity'}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'resultingMode'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            label={'Select Resulting Mode'}
                                                            options={commonService._staticData.testResultingMode}
                                                            placeholder={'Select Resulting Mode'}
                                                            valueKey={'code'}
                                                            required={true}
                                                            getOptionLabel={(option) => option?.title}
                                                            formikField={field}/>
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Field name={'biomarkerIds'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikSelectDropdownComponent
                                                    label={'Select Biomarker(s)'}
                                                    options={biomarkerList}
                                                    multiple={true}
                                                    placeholder={'Select Biomarkers(s)'}
                                                    valueKey={'id'}
                                                    isDataLoading={isBiomarkerListLoading}
                                                    isDataLoadingFailed={isBiomarkerListLoadingError}
                                                    isDataLoaded={isBiomarkerListLoaded}
                                                    required={true}
                                                    getOptionLabel={(option) => commonService.capitalizeFirstLetterOfEachWord(option.name)}
                                                    formikField={field}/>
                                            )
                                        }
                                    </Field>
                                    {/*<Field name={'description'}>*/}
                                    {/*    {*/}
                                    {/*        (field: FieldProps) => (*/}
                                    {/*            <FormikInputComponent*/}
                                    {/*                label={'Description (if any)'}*/}
                                    {/*                placeholder={'Enter Description'}*/}
                                    {/*                type={"text"}*/}
                                    {/*                required={false}*/}
                                    {/*                multiline={true}*/}
                                    {/*                rows={3}*/}
                                    {/*                formikField={field}*/}
                                    {/*            />*/}
                                    {/*        )*/}
                                    {/*    }*/}
                                    {/*</Field>*/}

                                    {/*<Grid container spacing={2}>*/}
                                    {/*    <Grid item xs={12} sm={12} md={6} xl={6}>*/}
                                    {/*        <div className="attachment-upload-item">*/}
                                    {/*            <div className="d-flex">*/}
                                    {/*                <div className="attachments-title">Panel Result Template*</div>*/}
                                    {/*                <div className="mrg-left-15 mrg-top-5">*/}
                                    {/*                    <TooltipComponent*/}
                                    {/*                        placement={'top'}*/}
                                    {/*                        tooltip={"Panel Result Template"}*/}
                                    {/*                        arrow={true}>*/}
                                    {/*                        <HelpIcon/>*/}
                                    {/*                    </TooltipComponent>*/}
                                    {/*                </div>*/}
                                    {/*            </div>*/}

                                    {/*            {!values.panelResultTemplate ? <>*/}
                                    {/*                    <input type={"file"}*/}
                                    {/*                           className={"display-none"}*/}
                                    {/*                           ref={panelResultTemplateFileRef}*/}
                                    {/*                           accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}*/}
                                    {/*                           onChange={(event) => {*/}
                                    {/*                               handleTemplateAttachmentUpload("panelResultTemplate", event);*/}
                                    {/*                           }}*/}
                                    {/*                    />*/}
                                    {/*                    <Grid container spacing={2}>*/}
                                    {/*                        <Grid item xs={12} sm={6} md={6}>*/}
                                    {/*                            <ButtonComponent*/}
                                    {/*                                variant={"contained"}*/}
                                    {/*                                fullWidth={false}*/}
                                    {/*                                handleClick={() => {*/}
                                    {/*                                    panelResultTemplateFileRef.current?.click()*/}
                                    {/*                                }}*/}
                                    {/*                                startIcon={<FileUploadIcon/>}*/}
                                    {/*                                type={'button'}>*/}
                                    {/*                                Upload*/}
                                    {/*                            </ButtonComponent>*/}
                                    {/*                        </Grid>*/}
                                    {/*                        <Grid item xs={12} sm={6} md={6}>*/}
                                    {/*                        </Grid>*/}
                                    {/*                    </Grid>*/}
                                    {/*                    {*/}
                                    {/*                        (_.get(touched, "frontImage") && !!(_.get(errors, "frontImage"))) &&*/}
                                    {/*                        <ErrorComponent*/}
                                    {/*                            errorText={(_.get(errors, "frontImage"))}/>*/}
                                    {/*                    }*/}
                                    {/*                </>*/}
                                    {/*                : <>*/}
                                    {/*                    <ChipComponent*/}
                                    {/*                        label={'Result Template.xls'}*/}
                                    {/*                        onDelete={() => {*/}
                                    {/*                            setFieldValue("panelResultTemplate", null);*/}
                                    {/*                        }}*/}
                                    {/*                        icon={<AttachFile/>}*/}
                                    {/*                    />*/}
                                    {/*                </>*/}
                                    {/*            }*/}
                                    {/*        </div>*/}
                                    {/*    </Grid>*/}

                                    {/*    <Grid item xs={12} sm={12} md={6} xl={6}>*/}
                                    {/*        <div className="attachment-upload-item">*/}
                                    {/*            <div className="d-flex">*/}
                                    {/*                <div className="attachments-title">Bulk Import Template*</div>*/}
                                    {/*                <div className="mrg-left-15 mrg-top-5">*/}
                                    {/*                    <TooltipComponent*/}
                                    {/*                        placement={'top'}*/}
                                    {/*                        tooltip={"Bulk Import Template"}*/}
                                    {/*                        arrow={true}>*/}
                                    {/*                        <HelpIcon/>*/}
                                    {/*                    </TooltipComponent>*/}
                                    {/*                </div>*/}
                                    {/*            </div>*/}

                                    {/*            {!values.bulkImportTemplate ? <>*/}
                                    {/*                    <input type={"file"}*/}
                                    {/*                           className={"display-none"}*/}
                                    {/*                           ref={bulkImportTemplateFileRef}*/}
                                    {/*                           accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}*/}
                                    {/*                           onChange={(event) => {*/}
                                    {/*                               handleTemplateAttachmentUpload("bulkImportTemplate", event);*/}
                                    {/*                           }}*/}
                                    {/*                    />*/}
                                    {/*                    <Grid container spacing={2}>*/}
                                    {/*                        <Grid item xs={12} sm={6} md={6}>*/}
                                    {/*                            <ButtonComponent*/}
                                    {/*                                variant={"contained"}*/}
                                    {/*                                fullWidth={false}*/}
                                    {/*                                handleClick={() => {*/}
                                    {/*                                    bulkImportTemplateFileRef.current?.click()*/}
                                    {/*                                }}*/}
                                    {/*                                startIcon={<FileUploadIcon/>}*/}
                                    {/*                                type={'button'}>*/}
                                    {/*                                Upload*/}
                                    {/*                            </ButtonComponent>*/}
                                    {/*                        </Grid>*/}
                                    {/*                        <Grid item xs={12} sm={6} md={6}>*/}
                                    {/*                        </Grid>*/}
                                    {/*                    </Grid>*/}
                                    {/*                    {*/}
                                    {/*                        (_.get(touched, "frontImage") && !!(_.get(errors, "frontImage"))) &&*/}
                                    {/*                        <ErrorComponent*/}
                                    {/*                            errorText={(_.get(errors, "frontImage"))}/>*/}
                                    {/*                    }*/}
                                    {/*                </>*/}
                                    {/*                : <>*/}
                                    {/*                    <ChipComponent*/}
                                    {/*                        label={'Bulk Import Template.xls'}*/}
                                    {/*                        onDelete={() => {*/}
                                    {/*                            setFieldValue("bulkImportTemplate", null);*/}
                                    {/*                        }}*/}
                                    {/*                        icon={<AttachFile/>}*/}
                                    {/*                    />*/}
                                    {/*                </>*/}
                                    {/*            }*/}
                                    {/*        </div>*/}
                                    {/*    </Grid>*/}
                                    {/*</Grid>*/}
                                </div>
                                <div className="form-action-section-edit">
                                    <ButtonComponent
                                        className={"width-25"}
                                        color={"secondary"}
                                        type={"button"}
                                        variant={"outlined"}
                                        disabled={isSubmitting}
                                        fullWidth={false}
                                        handleClick={() => {
                                            onEditCancel()
                                        }}
                                    >
                                        Cancel
                                    </ButtonComponent>
                                    <ButtonComponent
                                        className={"width-25"}
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={false}
                                        disabled={!(isValid && dirty)}
                                        isLoading={isSubmitting}
                                    >
                                        Save Changes
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }
                    }
                </Formik>
            </div>
        )
    }

;

export default TestDetailsEditComponent;
